import React, { useState, useEffect } from 'react';
import { X, FilePlus, Calendar, Clock, ListTodo, Repeat, Info, Sparkles, MessageSquare } from 'lucide-react';
import NoteEditor from './NoteEditor';
import RecurringFields from './RecurringFields';
import DueDateSelector from './DueDateSelector';
import { colorOptions } from './noteStyles';
import { getNextTaskId, createTaskId } from '../utils/taskIdUtils';

const TaskCreatorOverlay = ({
    isOpen,
    onClose,
    onCreateNote,
    defaultNoteColor,
    initialQuadrant,
    taskType: parentTaskType,
    defaultFields,
    aiRationale,
    isAIGenerated = false,
    embedded = false,
    ...props
}) => {
    const [taskType, setTaskType] = useState(parentTaskType);
    const [dateError, setDateError] = useState(null);
    const [isScheduled, setIsScheduled] = useState(false);

    const [editedFields, setEditedFields] = useState({
        title: defaultFields?.title || '',
        category: defaultFields?.category || 'Other',
        color: colorOptions.some(opt => opt.name === defaultFields?.color) ? defaultFields.color : defaultNoteColor,
        subtasks: defaultFields?.subtasks || [],
        quadrant: defaultFields?.quadrant || initialQuadrant,
        scheduledFor: null,
        dueAt: null,
        assignedDays: [],
    });

    // Reset fields when overlay opens
    useEffect(() => {
        if (isOpen) {
            setEditedFields({
                title: defaultFields?.title || '',
                category: defaultFields?.category || 'Other',
                color: defaultFields?.color || defaultNoteColor,
                subtasks: defaultFields?.subtasks || [],
                quadrant: defaultFields?.quadrant || initialQuadrant,
                scheduledFor: null,
                dueAt: null,
                assignedDays: []
            });
            setDateError(null);
            if (parentTaskType === 'scheduled') {
                setTaskType('active');
                setIsScheduled(true);
            } else {
                setTaskType(parentTaskType);
                setIsScheduled(false);
            }
        }
    }, [isOpen, defaultNoteColor, initialQuadrant, parentTaskType, defaultFields]);

    const validateDates = (scheduledFor, dueAt) => {
        if (isScheduled && !scheduledFor) {
            setDateError('Scheduled date is required when scheduling a task');
            return false;
        }

        if (scheduledFor && dueAt) {
            const scheduledDate = new Date(scheduledFor);
            const dueDate = new Date(dueAt);

            if (dueDate <= scheduledDate) {
                setDateError('Due date must be after the scheduled date');
                return false;
            }
        }

        setDateError(null);
        return true;
    };

    const handleTaskTypeChange = (newType) => {
        setTaskType(newType);
        setEditedFields(prev => ({
            ...prev,
            scheduledFor: null,
            dueAt: null,
            assignedDays: []
        }));
        setDateError(null);
        setIsScheduled(false);
    };

    const handleCreateNote = () => {
        if (isScheduled && !validateDates(editedFields.scheduledFor, editedFields.dueAt)) {
            return;
        }
        console.log(isScheduled, editedFields.scheduledFor);

        // Format subtasks to the correct structure
        const formattedSubtasks = editedFields.subtasks.map(task => ({
            id: task.id,
            text: task.text,
            checked: task.checked ?? false
        }));

        // Base note structure without dueAt
        const baseNote = {
            title: editedFields.title,
            category: editedFields.category,
            color: editedFields.color,
            quadrant: editedFields.quadrant,
            subtasks: formattedSubtasks,
            completedDates: [],
            lastCompletedAt: null
        };

        // Determine the effective task type based on scheduling status
        let effectiveTaskType;
        let finalNote;

        if (isScheduled) {
            effectiveTaskType = 'scheduled';
            finalNote = {
                ...baseNote,
                scheduledFor: editedFields.scheduledFor,
                dueAt: editedFields.dueAt,
                status: 'scheduled'
            };
        } else if (taskType === 'recurring') {
            effectiveTaskType = 'recurring';
            finalNote = {
                ...baseNote,
                assignedDays: editedFields.assignedDays
            };
        } else {
            effectiveTaskType = 'active';
            finalNote = {
                ...baseNote,
                dueAt: editedFields.dueAt
            };
        }

        onCreateNote(finalNote, effectiveTaskType);
        onClose();
    };

    const renderDateFields = () => {
        if (taskType === 'recurring') {
            return null;
        }

        return (
            <div className="space-y-4 mt-4">
                {taskType === 'active' && (
                    <div className="flex items-center mb-4">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={isScheduled}
                                onChange={(e) => {
                                    setIsScheduled(e.target.checked);
                                    if (!e.target.checked) {
                                        setEditedFields(prev => ({
                                            ...prev,
                                            scheduledFor: null
                                        }));
                                        setDateError(null);
                                    }
                                }}
                                className="form-checkbox h-4 w-4 text-purple-600"
                            />
                            <span className="ml-2 text-sm text-gray-700">Schedule this task for later</span>
                        </label>
                    </div>
                )}
                {isScheduled && (
                    <div className="relative">
                        <div className="flex justify-between items-start mb-2">
                            <label className="flex items-center mb-1.5 text-sm font-medium text-gray-700">
                                <Calendar size={18} className="mr-1.5" />
                                Schedule Date <span className="ml-0.5 text-red-500">*</span>
                            </label>
                            {/* <label className="flex items-center mb-1.5 text-sm font-medium text-gray-700">
                                <LayoutGrid size={18} className="mr-1.5" />
                                Quadrant
                            </label> */}
                            <div className="group relative">
                                <div className="flex items-center text-gray-500 text-sm cursor-help">
                                    <Info size={16} className="mr-1" />
                                    <span className="text-xs">What's this?</span>
                                </div>
                                <div className="invisible group-hover:visible absolute right-0 top-full mt-1 p-2 bg-white border rounded shadow-lg text-sm text-gray-600 z-10 w-64">
                                    The task will automatically become active at this date and time
                                </div>
                            </div>
                        </div>
                        <DueDateSelector
                            dueAt={editedFields.scheduledFor}
                            onChange={(newDate) => {
                                setEditedFields(prev => ({
                                    ...prev,
                                    scheduledFor: newDate,
                                    dueAt: prev.dueAt && new Date(prev.dueAt) <= new Date(newDate)
                                        ? null
                                        : prev.dueAt
                                }));
                                validateDates(newDate, editedFields.dueAt);
                            }}
                            onClear={() => {
                                setEditedFields(prev => ({ ...prev, scheduledFor: null }));
                                setDateError(null);
                            }}
                            isRequired={isScheduled}
                            isScheduledDate={true}
                            className="mb-4"
                        />
                    </div>
                )}

                <div className="relative">
                    <div className="flex justify-between items-start mb-2">
                        <label className="flex items-center mb-1.5 text-sm font-medium text-gray-700">
                            <Calendar size={18} className="mr-1.5" />
                            Due Date (Optional)
                        </label>
                        <div className="group relative">
                            <div className="flex items-center text-gray-500 text-sm cursor-help">
                                <Info size={16} className="mr-1" />
                                <span className="text-xs">What's this?</span>
                            </div>
                            <div className="invisible group-hover:visible absolute right-0 top-full mt-1 p-2 bg-white border rounded shadow-lg text-sm text-gray-600 z-10 w-64">
                                {isScheduled
                                    ? 'Optional deadline for the task. Must be after the scheduled date.'
                                    : 'Optional deadline for the task.'}
                            </div>
                        </div>
                    </div>
                    <DueDateSelector
                        dueAt={editedFields.dueAt}
                        onChange={(newDate) => {
                            setEditedFields(prev => ({ ...prev, dueAt: newDate }));
                            validateDates(editedFields.scheduledFor, newDate);
                        }}
                        onClear={() => {
                            setEditedFields(prev => ({ ...prev, dueAt: null }));
                            setDateError(null);
                        }}
                        isScheduledDate={false}
                        minDate={isScheduled && editedFields.scheduledFor
                            ? new Date(editedFields.scheduledFor)
                            : new Date()}
                    />
                </div>

                {dateError && (
                    <div className="text-red-500 text-sm mt-2 flex items-center">
                        <Info size={16} className="mr-1" />
                        {dateError}
                    </div>
                )}
            </div>
        );
    };

    const handleSubtaskEdit = (id, newText) => {
        setEditedFields(prev => ({
            ...prev,
            subtasks: prev.subtasks.map(task =>
                task.id === id ? { ...task, text: newText } : task
            )
        }));
    };

    const handleSubtaskDelete = (id) => {
        setEditedFields(prev => ({
            ...prev,
            subtasks: prev.subtasks.filter(task => task.id !== id)
        }));
    };

    const handleSubtaskAdd = () => {
        const nextId = getNextTaskId(editedFields.subtasks);
        setEditedFields(prev => ({
            ...prev,
            subtasks: [...prev.subtasks, {
                id: createTaskId(nextId),
                text: '',
                checked: false
            }]
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-purple-50 p-4 rounded-lg shadow-xl w-[540px] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold flex items-center">
                        {isAIGenerated ? (
                            <>
                                <Sparkles size={24} className="mr-2" />
                                AI Generated Task Plan
                            </>
                        ) : (
                            <>
                                <FilePlus size={24} className="mr-2" />
                                Create New Task
                            </>
                        )}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                {/* AI Rationale Section */}
                {isAIGenerated && aiRationale && (
                    <div className="mb-6">
                        <div className="flex items-center mb-2">
                            <MessageSquare size={18} className="mr-2 text-purple-600" />
                            <label className="text-sm font-medium text-gray-700">
                                AI Rationale
                            </label>
                        </div>
                        <div className="bg-white/50 rounded-lg p-3 text-sm text-gray-700">
                            {aiRationale}
                        </div>
                    </div>
                )}

                {/* Task Type Selector (hide for AI-generated tasks) */}
                {!isAIGenerated && (
                    <div className="flex space-x-2 mb-6 p-1 bg-gray-100 rounded-lg">
                        {[
                            { id: 'active', icon: ListTodo, label: 'Active Task' },
                            { id: 'recurring', icon: Repeat, label: 'Recurring Task' }
                        ].map(({ id, icon: Icon, label }) => (
                            <button
                                key={id}
                                onClick={() => handleTaskTypeChange(id)}
                                className={`flex-1 flex items-center justify-center space-x-2 p-2 rounded-md transition-colors
                                    ${taskType === id
                                        ? 'bg-white shadow text-purple-600'
                                        : 'hover:bg-white/50 text-gray-600'}`}
                            >
                                <Icon size={18} />
                                <span className="font-medium">{label}</span>
                            </button>
                        ))}
                    </div>
                )}

                <NoteEditor
                    editedFields={editedFields}
                    onFieldUpdate={(field, value) => setEditedFields(prev => ({ ...prev, [field]: value }))}
                    onSubtaskEdit={handleSubtaskEdit}
                    onSubtaskAdd={handleSubtaskAdd}
                    onSubtaskDelete={handleSubtaskDelete}
                    showQuadrantSelector={false}
                />

                {renderDateFields()}
                {taskType === 'recurring' && (
                    <RecurringFields
                        editedFields={editedFields}
                        setEditedFields={setEditedFields}
                    />
                )}

                {/* Updated buttons section */}
                <div className="flex justify-center mt-6 space-x-4">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 rounded-full border-2 border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                        Discard
                    </button>
                    <button
                        onClick={handleCreateNote}
                        disabled={isScheduled && !editedFields.scheduledFor}
                        className={`bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full
                            ${(!isScheduled || editedFields.scheduledFor)
                                ? 'hover:opacity-90 transition-opacity'
                                : 'opacity-50 cursor-not-allowed'}`}
                    >
                        {`Create ${taskType === 'recurring'
                            ? 'Recurring'
                            : isScheduled
                                ? 'Scheduled'
                                : 'Active'} Task`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaskCreatorOverlay;